
import { fill } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { GapStore, Gap } from "../../Model/ModelType.js";
import { SimulatedDesign__ComponentOfInstance_6546966D } from "../../Simulator/SimTypes.js";
import { ofArrayWithTail, tryFind } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { defaultOf, equals } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { PortType } from "../../Common/CommonTypes.js";
import { waveIndexOf, ofInstanceCached } from "../../Simulator/FastSim/PortView.js";
import { valueAt } from "./WaveData.js";
import { fromUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { tryFind as tryFind_1, isEmpty } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { defaultArg } from "../../fable_modules/fable-library-js.5.13.0/Option.js";
import { valueAtSample } from "./WaveDrawn.js";
import { UInt32ToPaddedString, BigIntToPaddedString } from "../../Simulator/NumberHelpers.js";
import { tooltipTextColour, tooltipBackgroundColor, tooltipShadowOpacity, waveLegendMaxChars } from "./WaveSimTypes.js";
import { getFastSim } from "../../Simulator/Simulator.js";
import { concat } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { SVGAttr, HTMLAttr } from "../../fable_modules/Fable.React.9.4.0/Fable.React.Props.fs.js";
import * as react from "react";
import { keyValueList } from "../../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { toString } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { warnOnce } from "../../Shared/Log.js";

/**
 * A gap in the wave simulator, represented by a start cycle and a length.
 * the only gaps that are stored are relevant are those that correspond to hatched
 * parts of the waveform in which the wave value is not printed.
 * A store with room for `maxGaps` runs and no run in progress.
 * 
 * GapStart and GapEnd are -1 for "nothing pending", not 0. Zero was indistinguishable from a run
 * starting at cycle 0, so a view whose first hatched gap was anywhere else opened by storing a
 * zero-length run - one slot spent on nothing. With the store sized for the most runs a view can
 * hold, that one extra entry was one past the end: harmless in JavaScript, where writing past an
 * array extends it, and an exception anywhere else.
 */
export function initGapStore(maxGaps) {
    return new GapStore(fill(new Array(maxGaps), 0, maxGaps, new Gap(0, 0)), 0, -1, -1);
}

/**
 * Add a gap to the store, extending the run in progress if this one continues it.
 * 
 * Gaps arrive in order, so a run ends as soon as one arrives that does not continue it.
 */
export function addGapToStore(store, gap) {
    if (store.GapEnd < 0) {
        store.GapStart = (gap.Start | 0);
        store.GapEnd = ((gap.Start + gap.Length) | 0);
    }
    else if (store.GapEnd === gap.Start) {
        store.GapEnd = ((store.GapEnd + gap.Length) | 0);
    }
    else {
        store.Gaps[store.NextGap] = (new Gap(store.GapStart, store.GapEnd - store.GapStart));
        store.NextGap = ((store.NextGap + 1) | 0);
        store.GapStart = (gap.Start | 0);
        store.GapEnd = ((gap.Start + gap.Length) | 0);
    }
}

/**
 * Store the run still in progress, if there is one.
 * 
 * There is nothing to merge with: a run is stored only when the gap that ended it started
 * somewhere else, so the run in progress never touches the one before it. The branch that tried to
 * merge them could not fire, and would have overwritten the stored run's start if it had.
 */
export function finaliseStore(store) {
    if (store.GapEnd >= 0) {
        store.Gaps[store.NextGap] = (new Gap(store.GapStart, store.GapEnd - store.GapStart));
        store.NextGap = ((store.NextGap + 1) | 0);
        store.GapStart = -1;
        store.GapEnd = -1;
    }
}

/**
 * Check if a wave is hatched at a given cycle.
 * This is done by checking if the cycle is within any of the gaps in a mutable store,
 * which is updated as gaps are added.
 */
export function checkIfHatched(store, cycle) {
    const array = store.Gaps.slice(0, (store.NextGap - 1) + 1);
    return array.some((gap) => {
        if (gap.Start <= cycle) {
            return (gap.Length + gap.Start) > cycle;
        }
        else {
            return false;
        }
    });
}

/**
 * Whether a memory's contents can change as a simulation runs.
 * 
 * A ROM's cannot: what it holds is part of its type. Only the location it is READING moves, which
 * is why its rows are worth fetching once and marking again as the cursor moves, while a RAM's
 * have to be fetched afresh for every cycle they are shown at.
 */
export function isReadOnlyMemory(fs, ram_, ram__1) {
    let matchValue;
    const option_1 = SimulatedDesign__ComponentOfInstance_6546966D(fs.Design, [ram_, ram__1]);
    matchValue = ((option_1 != null) ? option_1.Type : undefined);
    let matchResult;
    if (matchValue != null) {
        switch (matchValue.tag) {
            case 40:
            case 39: {
                matchResult = 0;
                break;
            }
            default:
                matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return true;
        default:
            return false;
    }
}

/**
 * A memory's address input, in the instance it is in - the wave that says what it is reading at
 * each cycle.
 * 
 * A fact about the elaborated instance, so it comes from `PortView`, like every other driver the
 * wave simulator uses. Fetched with the waveforms being drawn rather than asked for on demand:
 * both the things that want it - the tooltip below, written into the DOM by a mouse handler, and
 * the read marker on a ROM's rows, drawn in a render - happen where nothing can wait.
 * 
 * A WAVE and not a driver index, because a fetch asks for waves: naming a port is what lets the
 * simulator holding the data find it, and the index alone no longer names anything.
 */
export function addressWaveOf(fs, ram_, ram__1) {
    const ram = [ram_, ram__1];
    const path = ram[1];
    const option_1 = tryFind((p) => {
        if (equals(p.PortComp, ram[0]) && equals(p.PortIs, PortType.Input)) {
            return p.PortNum === 0;
        }
        else {
            return false;
        }
    }, ofInstanceCached(fs, path).ViewPorts);
    if (option_1 != null) {
        return waveIndexOf(path, option_1);
    }
    else {
        return undefined;
    }
}

/**
 * Where that address lies in the simulation that answered - the key the cache holds it under.
 */
export function addressDriverOf(fs, ram_, ram__1) {
    const ram = [ram_, ram__1];
    const option_1 = addressWaveOf(fs, ram[0], ram[1]);
    if (option_1 != null) {
        return option_1.SimArrayIndex;
    }
    else {
        return undefined;
    }
}

/**
 * The cycle at which a memory's address input says what it is reading, for a location shown at
 * `step`. A write lands one clock after the address that caused it, and a synchronous read
 * presents its data a clock late; an asynchronous one does not. RamView.readAndWritten draws the
 * same distinction on the other side of the wire.
 */
export function addressReadStep(fs, ram_, ram__1, step) {
    let matchValue;
    const option_1 = SimulatedDesign__ComponentOfInstance_6546966D(fs.Design, [ram_, ram__1]);
    matchValue = ((option_1 != null) ? option_1.Type : undefined);
    let matchResult;
    if (matchValue != null) {
        switch (matchValue.tag) {
            case 39:
            case 42: {
                matchResult = 0;
                break;
            }
            case 40:
            case 41: {
                matchResult = 1;
                break;
            }
            default:
                matchResult = 2;
        }
    }
    else {
        matchResult = 2;
    }
    switch (matchResult) {
        case 0:
            return step;
        case 1:
            if (step > 0) {
                return step - 1;
            }
            else {
                return undefined;
            }
        default:
            return undefined;
    }
}

/**
 * The location a memory is reading at `step`, from whichever simulator is running.
 */
export function addressReadAt(fs, ram_, ram__1, step) {
    let matchValue_3;
    const ram = [ram_, ram__1];
    const matchValue = addressDriverOf(fs, ram[0], ram[1]);
    const matchValue_1 = addressReadStep(fs, ram[0], ram[1], step);
    let matchResult, driver, readStep;
    if (matchValue != null) {
        if (matchValue_1 != null) {
            matchResult = 0;
            driver = matchValue;
            readStep = matchValue_1;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0: {
            const option_1 = valueAt(driver, readStep);
            if (option_1 != null) {
                return (matchValue_3 = option_1.Dat, (matchValue_3.tag === 1) ? matchValue_3.fields[0] : fromUInt32(matchValue_3.fields[0]));
            }
            else {
                return undefined;
            }
        }
        default:
            return undefined;
    }
}

/**
 * The memory this wave is the data output of, when it has comments worth showing, together with
 * the driver of the address it reads.
 * 
 * Which memory it is and what its .ram file said are facts about the component as DRAWN, so they
 * come from the design. Where its address lies is a fact about the elaborated instance, so it
 * comes from `PortView` - like every other width and driver the wave simulator uses.
 * 
 * Returned as a pair because both callers want both: the tooltip below, to write the comment, and
 * WaveSimTop.missingForWaves, to fetch the address wave along with the ones being drawn. Fetched
 * with them rather than on hover because a tooltip is written into the DOM by a mouse handler,
 * which cannot wait for anything - so the address has to be there before the pointer arrives.
 */
export function romAddressOf(fs, wave) {
    if (!equals(wave.WaveId.PortType, PortType.Output)) {
        return undefined;
    }
    else {
        let option_7;
        let option_3;
        const option_1 = SimulatedDesign__ComponentOfInstance_6546966D(fs.Design, wave.WaveId.Id);
        if (option_1 != null) {
            const matchValue = option_1.Type;
            let matchResult, mem;
            switch (matchValue.tag) {
                case 39: {
                    matchResult = 0;
                    mem = matchValue.fields[0];
                    break;
                }
                case 40: {
                    matchResult = 0;
                    mem = matchValue.fields[0];
                    break;
                }
                default:
                    matchResult = 1;
            }
            switch (matchResult) {
                case 0: {
                    option_3 = mem.Comments;
                    break;
                }
                default:
                    option_3 = undefined;
            }
        }
        else {
            option_3 = undefined;
        }
        option_7 = ((option_3 != null) ? (!isEmpty(option_3) ? option_3 : undefined) : undefined);
        if (option_7 != null) {
            const comments_1 = option_7;
            let option_5;
            const tupledArg = wave.WaveId.Id;
            option_5 = addressWaveOf(fs, tupledArg[0], tupledArg[1]);
            if (option_5 != null) {
                return [option_5, comments_1];
            }
            else {
                return undefined;
            }
        }
        else {
            return undefined;
        }
    }
}

/**
 * The comment written in a .ram file against the location a ROM is reading at a given simulation
 * step, if the ROM has any comments and this wave is its data output.
 * 
 * The location read is the address input one step earlier for a synchronous ROM and at the same
 * step for an asynchronous one, which is the distinction RamView.readAndWritten also makes.
 * 
 * The address comes from `WaveData`, which answers from whichever simulator is running - the local
 * arrays where the renderer is simulating, and the window fetched from the sidecar where it is
 * not. It used to be read straight out of the renderer's own FastSimulation, which in .NET mode is
 * built for its structure and never run: every address would have been an unrun array's zero, so
 * the comment against location zero was shown on every hover, confidently. That was guarded by
 * refusing to answer at all, which is why these comments disappeared in that mode.
 * 
 * None where the address is not among the samples held - a synchronous ROM's address is one CYCLE
 * back, and at a zoom where a column is several cycles wide that is not a sample that was fetched.
 * No comment is better than the wrong one.
 */
export function getRomCommentAtStep(fs, step, wave) {
    let option_1, tupledArg;
    const matchValue = romAddressOf(fs, wave);
    if (matchValue != null) {
        const comments = matchValue[1];
        return defaultArg((option_1 = ((tupledArg = wave.WaveId.Id, addressReadAt(fs, tupledArg[0], tupledArg[1], step))), (option_1 != null) ? tryFind_1(option_1, comments) : undefined), "");
    }
    else {
        return "";
    }
}

/**
 * Text for the tooltip shown when hovering a waveform at a given cycle, or "" for no tooltip.
 * A value is given when the wave is too narrow there to have its number printed on it - the cached
 * "gap" data says when that is - and a ROM's data output also gives the comment written against
 * the location being read, so that both appear when the number is hidden as well.
 * 
 * The wave is passed in rather than counted off SelectedWaves by row number. The rows drawn are
 * the selected waves that WaveDetails still HOLDS - see WaveSimStyle.selectedWaves, which the three
 * columns are all built from - so counting rows off SelectedWaves itself answered for a different
 * row wherever the two differ, and indexed WaveDetails with a key it might not have.
 * Everything is asked of the waveform AS DRAWN - the gaps in it, the samples it was drawn from,
 * and the cycle they are of. What is under the pointer is a column of a picture, and the picture
 * may be a window behind the controls while its data is still on its way; the tooltip describes
 * what is there rather than what has been asked for.
 * 
 * `sample` is therefore an index into that picture, counted from its left edge, and NOT a clock
 * cycle. It used to be given the absolute display cycle and to test it against gaps recorded from
 * the window's left edge, so a viewer scrolled anywhere but cycle 0 asked about the wrong column.
 */
export function getWaveToolTip(sample, drawn, wave, ws) {
    let this$, option_1, fd, matchValue;
    const window$ = drawn.Spec.Window;
    const cycle = (((this$ = window$, this$.StartSample * this$.Multiplier)) + (sample * window$.Multiplier)) | 0;
    const hiddenValue = checkIfHatched(drawn.Gaps, drawn.Spec.Window.StartSample + sample) ? defaultArg((option_1 = valueAtSample(drawn, sample), (option_1 != null) ? ((fd = option_1, (matchValue = fd.Dat, (matchValue.tag === 1) ? BigIntToPaddedString(waveLegendMaxChars, ws.Radix, fd.Width, matchValue.fields[0]) : UInt32ToPaddedString(waveLegendMaxChars, ws.Radix, fd.Width, matchValue.fields[0])))) : undefined), "") : "";
    const matchValue_1 = getRomCommentAtStep(getFastSim(), cycle, wave);
    if (hiddenValue === "") {
        if (matchValue_1 === "") {
            return "";
        }
        else {
            return matchValue_1;
        }
    }
    else if (matchValue_1 === "") {
        return concat("Value:", hiddenValue);
    }
    else {
        return concat("Value:", hiddenValue, ". ", matchValue_1);
    }
}

/**
 * SVG group element for tooltip.
 * The props of the tooltip, as well as its text, are set in the function <c>changeToolTip</c>.
 * Initila props make it invisible.
 */
export function evilSvgToolTip(tipName, ws, tipText, textProps) {
    let props, props_2, props_4;
    const props_6 = [new HTMLAttr(/* Id */ 99, [tipName + "Group"])];
    const children_6 = [(props = [new HTMLAttr(/* Id */ 99, [tipName + "Rect2"]), new SVGAttr(/* Width */ 41, [50]), new SVGAttr(/* Height */ 14, [20]), new SVGAttr(/* Fill */ 6, ["black"]), new SVGAttr(/* Opacity */ 19, [tooltipShadowOpacity]), ["style", {
        visibility: "hidden",
    }]], react.createElement("rect", keyValueList(props, 1))), (props_2 = [new HTMLAttr(/* Id */ 99, [tipName + "Rect1"]), new SVGAttr(/* Width */ 41, [50]), new SVGAttr(/* Height */ 14, [20]), new SVGAttr(/* Fill */ 6, [tooltipBackgroundColor]), new SVGAttr(/* Opacity */ 19, [1]), ["style", {
        visibility: "hidden",
    }]], react.createElement("rect", keyValueList(props_2, 1))), (props_4 = ofArrayWithTail([new HTMLAttr(/* Id */ 99, [tipName + "Text"]), new SVGAttr(/* Fill */ 6, [tooltipTextColour]), new SVGAttr(/* Opacity */ 19, ["1.0"])], textProps), react.createElement("text", keyValueList(props_4, 1), tipText))];
    return react.createElement("g", keyValueList(props_6, 1), ...children_6);
}

/**
 * Change the tooltip text and position.
 */
export function changeToolTip(tipName, tipText, xPos, yPos, ttXMaxEdge, isVisible) {
    const textSvgName = tipName + "Text";
    const svgText = document.getElementById(textSvgName);
    const changeShape = (shapeId, w, x, y, show) => {
        const shape = document.getElementById(shapeId);
        shape.setAttributeNS("", "width", toString(w));
        shape.setAttributeNS("", "x", toString(x));
        shape.setAttributeNS("", "y", toString(y));
        shape.setAttributeNS("", "style", show ? "visibility: visible" : "visibility: hidden");
    };
    if (equals(svgText, defaultOf())) {
        warnOnce("tooltip-missing-element", concat("the DOM element \'", textSvgName, "\' needed by changeToolTip is not there"));
    }
    else {
        svgText.textContent = tipText;
        const w_1 = svgText.getComputedTextLength();
        const adjXPos = (((xPos + w_1) + 10) > ttXMaxEdge) ? ((ttXMaxEdge - w_1) - 10) : xPos;
        changeShape(tipName + "Rect1", w_1 + 10, adjXPos, yPos, isVisible);
        changeShape(tipName + "Rect2", w_1 + 10, adjXPos + 2, yPos + 2, isVisible);
        changeShape(textSvgName, w_1, adjXPos + 5, yPos + 16, isVisible);
    }
}

